import { consoleLog } from '@Utils/consoleLog';
import { measureLayoutAsync } from '@Utils/measure-layout-async';
import { injectable } from 'inversify';
import { action, when } from 'mobx';
import type { RefObject } from 'react';
import { createRef } from 'react';
import type { ScrollView, View } from 'react-native';

import container from '~ioc/ioc';

// Allows component of a ScrollView to register their positions inside a ScrollView for easier scrolling
@injectable()
export class ScrollViewManager {
  scrollViewRef = createRef<ScrollView>(); // the scroll View to use for performing the scroll
  parentRef = createRef<View>(); // this View should be the root View of the ScrollView. Should be the same size as the scrollable area
  private markers = new Map<string, View>();

  @action updateMarker = (id: string, ref: View | null) => {
    if (!ref) {
      this.deleteMarker(id);
    } else {
      this.markers.set(id, ref);
    }
  };

  @action deleteMarker = (id: string) => {
    this.markers.delete(id);
  };

  @action scrollToId = async (id: string) => {
    try {
      // give some time for the layout measurement to be registered if not already
      // Can be common case with 'autoFocus'
      await when(() => Boolean(this.markers.get(id)), {
        timeout: 1000,
      });
    } catch {
      consoleLog(
        `Scroll failed. No Product panel scroll marker found for id: ${id}`
      );
      return;
    }

    const viewComponent = this.markers.get(id);
    const { y } = await measureLayoutAsync(
      { current: viewComponent! },
      this.parentRef as RefObject<View>
    );

    this.scrollViewRef.current?.scrollTo({ y, animated: true });
  };
}

container.bind(ScrollViewManager).toSelf().inTransientScope();
